import {
  getAppType,
  getNativeAppVersion,
  getNativeBuildNumber,
  getNativeChannel,
  getWebVersion,
} from '@/shared/bridge';

/**
 * 알약 배지 한 줄 출력 — 강조부(`bold`)와 부차부(`dim`)를 이어붙여 하나의 상자로 보이게.
 *
 * **개행 금지** — 콘솔은 `\n` 에서 로그 줄을 쪼개고 줄마다 배경을 다시 그려서 상자가 둘로 갈라진다
 * (`display:inline-block` 으로도 안 붙는다). 한 사각형을 원하면 한 줄로 둔다.
 *
 * 굵기·글자색이 달라 %c 조각이 둘로 나뉘는데, 조각마다 배경이 독립이라 그라데이션이 경계에서 끊긴다
 * → **앞 조각의 끝 색(`mid`)을 뒤 조각의 시작 색으로 물려** 이음매를 지운다. 모서리 반경은 양 끝에만.
 *
 * ⚠️ 각도는 반드시 `to right` — 대각선(`135deg`)이면 조각마다 자기 대각선 위에서 색을 다 소모해
 * 경계의 양쪽 색이 어긋나고, 강조부 뒤에서 그라데이션이 뚝 잘린 것처럼 보인다(실측).
 */
function printBadge(bold: string, dim: string, from: string, mid: string, to: string): void {
  // 가로 패딩은 **바깥 12 / 안쪽 5** 로 비대칭. 두 조각에 `padding:10px` 을 그대로 주면 조각1 의 오른쪽과
  // 조각2 의 왼쪽이 겹쳐 가운데만 20px 로 벌어진다(가장자리의 2배) — 안쪽만 줄여 낱말 간격처럼 보이게.
  const pill = 'padding-top:10px;padding-bottom:10px;font-size:11px;line-height:20px;color:#fff';
  console.log(
    `%c${bold}%c${dim}`,
    `${pill};padding-left:12px;padding-right:5px;font-weight:700;letter-spacing:.4px;background:linear-gradient(to right,${from},${mid});border-radius:10px 0 0 10px`,
    // 부차 정보 → weight normal + 글자만 알파로 눌러 배경에 녹인다.
    `${pill};padding-left:5px;padding-right:12px;font-weight:400;color:rgba(255,255,255,.72);background:linear-gradient(to right,${mid},${to});border-radius:0 10px 10px 0`,
  );
}

/** `getAppType()` 의 wire 값 → 표시용 플랫폼 라벨. */
const PLATFORM_LABEL: Record<string, string> = { ios: 'iOS', android: 'Android' };

/**
 * 부팅 시 콘솔에 찍는 버전 배너 — CS 대응의 1차 단서("F12 → 콘솔 첫 줄 캡처해 주세요").
 * 전 환경 공통(운영 포함) — 이미 번들에 박힌 공개값이라 새로 노출되는 정보는 없다.
 *
 * **두 축을 각각 한 줄로** 찍는다. native 셸 안에서 웹 번들은 OTA 로 따로 갈리므로 앱(셸) 버전과
 * 별개 축이고, 한 배지에 섞으면 어느 쪽 버전인지 읽는 사람이 헷갈린다 → 색까지 달리해 분리한다.
 *
 * - **주황(항상)** = 웹 번들 — 버전(`env.appVersion` ← `apps/web/package.json`) + 빌드 시각.
 *   빌드 시각을 같이 두는 이유: **배포는 버전을 올리지 않아**(범프는 `bump-version` 전담) 같은
 *   semver 로 여러 번 배포된다 → 버전만으로는 어느 배포인지 특정할 수 없다.
 * - **파랑(native 실행일 때만)** = 앱 셸 — 버전 + 빌드넘버 + EAS 채널. 웹과 달리 빌드 시각이 없고,
 *   같은 semver 로 여러 번 빌드되므로 **빌드넘버가 실제 식별자**다. 채널은 preview 빌드 식별의
 *   유일한 단서(번들 ID·env 를 production 과 공유). 관련 ADR: deploy.
 *
 * 빌드넘버(`Application.nativeBuildVersion`)는 iOS `CFBundleVersion` / Android `versionCode` 로
 * 출처가 다르고, `eas.json` 의 `appVersionSource: remote` + `autoIncrement` 때문에 **번들 ID·플랫폼마다
 * 독립 카운터**다 — 같은 앱이라도 iOS 8 / Android 25 처럼 값이 어긋나는 게 정상. 그래서 배지는
 * 플랫폼 라벨을 앞에 박는다(빌드넘버만으로는 어느 바이너리인지 못 가린다).
 *
 * @example 웹(데스크톱 브라우저) — 주황 한 줄
 * ☀️ HEVITON v2.0.0   Built at 2026-08-13 13:21:12
 *
 * @example iOS TestFlight(preview 채널) — 주황 + 파랑
 * ☀️ HEVITON v2.0.0   Built at 2026-08-13 13:21:12
 * 📱 iOS APP v2.0.1   build 8 · preview
 *
 * @example Android 운영 스토어 빌드
 * ☀️ HEVITON v2.0.0   Built at 2026-08-13 13:21:12
 * 📱 Android APP v2.0.1   build 25 · production
 *
 * @example Android 로컬 개발(`pnpm app:dev`, EAS Update 미구성)
 * ☀️ HEVITON v2.0.0   Built at 2026-08-13 13:21:12
 * 📱 Android APP v2.0.1   build 203 · local
 */
export function printVersionBanner(): void {
  printBadge(
    `☀️ HEVITON v${getWebVersion()}`,
    `Built at ${__BUILD_TIME__}`,
    '#c2410c',
    '#d96220',
    '#fb923c',
  );

  // web 실행이면 앱 축 자체가 없다 — 배지도 생략.
  const appVersion = getNativeAppVersion();
  if (!appVersion) return;

  const appType = getAppType();
  // 채널 null = EAS Update 미구성 = `pnpm app:dev` 로 Metro 에 붙은 로컬 빌드(native 쪽
  // `Updates.channel || undefined`). 빈칸으로 두면 「못 읽었다」와 섞이므로 이름을 박는다.
  const channel = getNativeChannel() ?? 'local';
  printBadge(
    `📱 ${PLATFORM_LABEL[appType] ?? appType} APP v${appVersion}`,
    `build ${getNativeBuildNumber() ?? '?'} · ${channel}`,
    '#1e40af',
    '#2563eb',
    '#60a5fa',
  );
}
